import { useContext, useMemo } from 'react'
import {
	ArchiveBoxIcon,
	ArrowsRightLeftIcon,
	CalendarDaysIcon,
	CheckCircleIcon,
	ExclamationTriangleIcon,
	EyeIcon,
	HeartIcon,
	ShoppingBagIcon,
	XCircleIcon
} from '@heroicons/react/24/outline'
import {
	HistoryButton,
	BackButton,
	Header,
	ConfirmModal,
	NotFound,
	Select
} from 'shared/components'
import { useTranslation } from 'libs/i18n'
import { NavLink, useParams, useSearchParams } from 'libs/router'
import { BookingSalesTable } from './organisms/Sales/Sales'
import { ProductsTable } from './organisms/ProductsTable/ProductsTable'
import { BookingForm } from './organisms/Form/Form'
import { BookingSummary } from './organisms/Summary/BookingSummary'
import { routes } from 'config/routes'
import { useFetchBooking } from './queries/useFetchBooking'
import { LifecycleItem } from './atoms/LifecycleItem/LifecycleItem'
import { CompletionPayout } from './organisms/CompletionPayout/CompletionPayout'
import { useMarkAsArrived } from './mutations/useMarkAsArrived'
import { useMarkAsNoShow } from './mutations/useMarkAsNoShow'
import { useMarkAsCancelled } from './mutations/useMarkAsCancelled'
import { useMarkAsCompleted } from './mutations/useMarkAsCompleted'
import { useMarkAsOverdue } from './mutations/useMarkAsOverdue'
import { useMarkAsAbandoned } from './mutations/useMarkAsAbandoned'
import { BOOKING_STATUS } from 'features/bookings/molecules/BookingStatus/BookingStatus'
import { useMarkAsTakeDownNotified } from './mutations/useMarkAsTakeDownNotified'
import { StoreSettingsContext } from 'store'
import { useForceComplete } from './mutations/useForceComplete'
import { differenceInDays, format } from 'libs/dates'
import { useModal, useSelect, useUserRoles } from 'shared/hooks'
import { cn } from 'utils/style'
import { Controller, useForm } from 'libs/form'
import { useFetchBookings } from './queries/useFetchBookings'
import { useProductTransfer } from './mutations/useProductTransfer'
import { enGB, is } from 'date-fns/locale'
import { useProductTransferValidationSchema } from './hooks/useProductTrasfer.schema'

type StatusTone = 'success' | 'warning' | 'danger'

type StatusButtonProps = {
	icon: React.ReactNode
	label: string
	active?: boolean
	tone?: StatusTone
	disabled: boolean
	onClick: () => void
}

const activeToneClassNames: Record<StatusTone, string> = {
	success: 'border-success/40 bg-success-soft text-success',
	warning: 'border-warning/40 bg-warning-soft text-warning',
	danger: 'border-danger/40 bg-danger-soft text-danger'
}

const StatusButton = ({
	icon,
	label,
	active = false,
	tone = 'success',
	disabled,
	onClick
}: StatusButtonProps) => (
	<button
		type="button"
		aria-pressed={active}
		disabled={disabled}
		className={cn(
			'flex h-10 w-full items-center justify-center gap-2 rounded-md border text-[13px] font-semibold transition-colors disabled:cursor-not-allowed [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-2',
			active
				? activeToneClassNames[tone]
				: 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:hover:bg-card disabled:hover:text-muted-foreground'
		)}
		onClick={onClick}>
		{icon}
		{label}
	</button>
)

const outlineButton =
	'flex h-9 w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground shadow transition-colors hover:bg-muted [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-2'

const BookingPage = () => {
	const { t, i18n } = useTranslation()
	const language = i18n.language === 'is' ? is : enGB
	const params = useParams()
	const [searchParams] = useSearchParams()
	const { booking, isBookingNotFound } = useFetchBooking()
	const { isUser } = useUserRoles()
	const { bookings } = useFetchBookings()
	const productTransferResolver = useProductTransferValidationSchema()
	const status = booking?.data.status

	const {
		handleSubmit,
		control,
		setValue,
		trigger,
		clearErrors,
		formState: { errors }
	} = useForm({
		resolver: productTransferResolver,
		mode: 'onChange'
	})

	const { handleSelectChange } = useSelect({ setValue, trigger })

	const {
		isOpen: isNoShowModalOpen,
		openModal: openNoShowModal,
		closeModal: closeNoShowModal
	} = useModal()
	const {
		isOpen: isCancelledModalOpen,
		openModal: openCancelledModal,
		closeModal: closeCancelledModal
	} = useModal()
	const {
		isOpen: isCompletedModalOpen,
		openModal: openCompletedModal,
		closeModal: closeCompletedModal
	} = useModal()
	const {
		isOpen: isOverdueModalOpen,
		openModal: openOverdueModal,
		closeModal: closeOverdueModal
	} = useModal()
	const {
		isOpen: isAbandonedModalOpen,
		openModal: openAbandonedModal,
		closeModal: closeAbandonedModal
	} = useModal()
	const {
		isOpen: isTakedownBookedModalOpen,
		openModal: openTakedownBookedModal,
		closeModal: closeTakedownBookedModal
	} = useModal()
	const {
		isOpen: isMoveUnsoldProductsModalOpen,
		openModal: openMoveUnsoldProductsModal,
		closeModal: closeMoveUnsoldProductsModal
	} = useModal()

	const { markAsArrivedMutation } = useMarkAsArrived({ id: booking?.data.id })
	const { markAsNoShowMutation } = useMarkAsNoShow({
		id: booking?.data.id,
		closeModal: closeNoShowModal
	})
	const { markAsCancelledMutation } = useMarkAsCancelled({
		id: booking?.data.id,
		closeModal: closeCancelledModal
	})
	const { markAsCompletedMutation } = useMarkAsCompleted({
		id: booking?.data.id,
		closeModal: closeCompletedModal
	})
	const { markAsOverdueMutation } = useMarkAsOverdue({
		id: booking?.data.id,
		closeModal: closeOverdueModal
	})
	const { markAsAbandonedMutation } = useMarkAsAbandoned({
		id: booking?.data.id,
		closeModal: closeAbandonedModal
	})
	const { markAsTakeDownNotified } = useMarkAsTakeDownNotified({
		id: booking?.data.id,
		closeModal: closeTakedownBookedModal
	})
	const { forceComplete } = useForceComplete({
		id: booking?.data.id,
		closeModal: closeCompletedModal
	})

	const { productTransfer } = useProductTransfer({
		id: booking?.data.id,
		clear: () => {
			closeMoveUnsoldProductsModal()
			setValue('targetBooking', undefined)
		}
	})

	const withBookingId = (action: (id: number) => void) => () => {
		if (typeof booking?.data.id === 'number') action(booking.data.id)
	}

	const handleArrived = withBookingId(markAsArrivedMutation)
	const handleNoShow = withBookingId(markAsNoShowMutation)
	const handleCancelled = withBookingId(markAsCancelledMutation)
	const handleOverdue = withBookingId(markAsOverdueMutation)
	const handleAbandoned = withBookingId(markAsAbandonedMutation)
	const handleTakedown = withBookingId(markAsTakeDownNotified)
	const handleCompleted = withBookingId((id) => {
		if (!booking?.data.is_active && status === 'CONFIRMED') {
			forceComplete(id)
		} else {
			markAsCompletedMutation(id)
		}
	})

	const handleProductTransfer = (targetId: string) => {
		if (typeof booking?.data.id === 'number') productTransfer(targetId)
	}

	const blockEverything = useMemo(() => {
		if (!status) return false

		return [BOOKING_STATUS.CANCELLED, BOOKING_STATUS.COMPLETED, BOOKING_STATUS.ABANDONED].includes(
			status as 'COMPLETED' | 'CANCELLED' | 'ABANDONED'
		)
	}, [status])

	const storeCtx = useContext(StoreSettingsContext)
	const currency = storeCtx?.storeSettings?.currency
	const lifecycle = booking?.data.lifecycle

	const storedDays = useMemo(() => {
		const maxStorageDay = lifecycle?.product_storage_max_days_applied as number
		const targetDate = new Date(lifecycle?.products_stored_at as string)
		const difference = differenceInDays(new Date(), targetDate)

		return maxStorageDay < difference ? maxStorageDay : difference
	}, [lifecycle?.products_stored_at, lifecycle?.product_storage_max_days_applied])

	const isTakedownBooked = !!lifecycle?.customer_notified_takedown_at

	const onSubmit = (data: any) => {
		handleProductTransfer(data?.targetBooking?.value)
	}

	const storageDetails =
		lifecycle?.product_storage_max_days_applied && lifecycle?.products_stored_at
			? `${storedDays} ${t('days')}${
					lifecycle.product_storage_fee_per_day_applied
						? ` · ${+lifecycle.product_storage_fee_per_day_applied * storedDays} ${currency} (${lifecycle.product_storage_fee_per_day_applied} ${currency}/${t('day')})`
						: ''
				}`
			: undefined

	const completedDetails = lifecycle?.booth_takedown_fee_applied
		? `${t('Cost:')} ${lifecycle.booth_takedown_fee_applied} ${currency}${
				lifecycle.customer_notified_takedown_at ? ` · ${t('Booked takedown')}` : ''
			}`
		: undefined

	if (isBookingNotFound) {
		return (
			<NotFound
				title={t('Booking not found')}
				description={t('The booking “{{ id }}” does not exist.', { id: params.id })}
				backTo={routes.bookingIndex}
				backText={t('Back to bookings')}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Booking #{{ id }}', { id: booking?.data.ref_nr ?? params.id })}
				eyebrow={t('Bookings')}
				actions={
					<>
						<BackButton
							text={t('Back to bookings')}
							to={
								searchParams.toString()
									? `${routes.bookingIndex}?${searchParams.toString()}`
									: routes.bookingIndex
							}
						/>
						<HistoryButton to={routes.bookingHistory(params.id)} />
					</>
				}
			/>
			<div className="mx-auto max-w-[1336px] space-y-5 px-5 lg:px-0">
				{booking?.data && <BookingSummary booking={booking.data} />}

				<div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
					<div className="min-w-0 space-y-5">
						<BookingForm booking={booking} />
						<BookingSalesTable />
						<ProductsTable />
					</div>

					<div className="space-y-5">
						{!isUser && (
							<section className="rounded-lg border border-border bg-card p-5 shadow-panel">
								<h2 className="font-display text-base font-semibold text-foreground">
									{t('Status')}
								</h2>
								<p className="text-xs text-muted-foreground">
									{t('Update where this booking stands.')}
								</p>
								<div className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
									{t('Arrival status')}
								</div>
								<div className="mt-2 space-y-2">
									<StatusButton
										icon={<CheckCircleIcon />}
										label={t('Arrived')}
										active={status === 'ARRIVED'}
										disabled={blockEverything}
										onClick={handleArrived}
									/>
									<StatusButton
										icon={<ExclamationTriangleIcon />}
										label={t('No show')}
										active={status === 'NO_SHOW'}
										tone="warning"
										disabled={blockEverything}
										onClick={openNoShowModal}
									/>
									<StatusButton
										icon={<XCircleIcon />}
										label={t('Cancelled')}
										active={status === 'CANCELLED'}
										tone="danger"
										disabled={blockEverything}
										onClick={openCancelledModal}
									/>
								</div>
								<div className="mt-5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
									{t('Area status')}
								</div>
								<div className="mt-2 space-y-2">
									<StatusButton
										icon={<ShoppingBagIcon />}
										label={t('Picked up')}
										disabled={blockEverything}
										onClick={openCompletedModal}
									/>
									<StatusButton
										icon={<ArchiveBoxIcon />}
										label={t('Storage')}
										disabled={blockEverything}
										onClick={openOverdueModal}
									/>
									<StatusButton
										icon={<HeartIcon />}
										label={t('Donated')}
										disabled={blockEverything}
										onClick={openAbandonedModal}
									/>
								</div>
							</section>
						)}

						<div className="space-y-2">
							<NavLink
								to={`${routes.productIndex}?booking_id=${params.id}`}
								className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-success px-4 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-success/90 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-2">
								<>
									<EyeIcon />
									{t('See all products')}
								</>
							</NavLink>
							{!isUser && (
								<button
									type="button"
									disabled={blockEverything}
									aria-pressed={isTakedownBooked}
									className={cn(
										outlineButton,
										'disabled:cursor-not-allowed disabled:opacity-50',
										isTakedownBooked &&
											'border-warning/40 bg-warning-soft text-warning hover:bg-warning-soft'
									)}
									onClick={openTakedownBookedModal}>
									<CalendarDaysIcon />
									{t('Book takedown')}
								</button>
							)}
							{isUser && (
								<button
									type="button"
									className={outlineButton}
									onClick={openMoveUnsoldProductsModal}>
									<ArrowsRightLeftIcon />
									{t('Move unsold products')}
								</button>
							)}
						</div>

						<section className="rounded-lg border border-border bg-card p-5 shadow-panel">
							<h2 className="font-display text-base font-semibold text-foreground">
								{t('Lifecycle')}
							</h2>
							<ol className="mt-4 space-y-1">
								<LifecycleItem
									label={t('Booking confirmed')}
									date={lifecycle?.booking_confirmed_at}
								/>
								<LifecycleItem label={t('Arrived')} date={lifecycle?.customer_arrived_at} />
								<LifecycleItem label={t('No-show')} date={lifecycle?.customer_no_show_at} />
								<LifecycleItem label={t('Cancelled')} date={lifecycle?.booking_cancelled_at} />
								<LifecycleItem label={t('Picked up')} date={lifecycle?.customer_picked_up_at} />
								<LifecycleItem
									label={t('Stored')}
									date={lifecycle?.products_stored_at}
									details={storageDetails}
								/>
								<LifecycleItem label={t('Donated')} date={lifecycle?.products_donated_at} />
								<LifecycleItem
									label={t('Completed')}
									date={lifecycle?.customer_picked_up_at || lifecycle?.products_donated_at}
									details={completedDetails}
								/>
								<LifecycleItem
									label={t('Booked takedown')}
									date={lifecycle?.customer_notified_takedown_at}
								/>
							</ol>
						</section>

						{booking?.data && <CompletionPayout booking={booking.data} />}
					</div>
				</div>
			</div>

			{isNoShowModalOpen ? (
				<ConfirmModal
					title={t('Request no show')}
					isOpened={isNoShowModalOpen}
					closeModal={closeNoShowModal}
					confirm={handleNoShow}>
					<p>
						{t(
							'You are about to request "no show" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isCancelledModalOpen ? (
				<ConfirmModal
					title={t('Request cancel')}
					isOpened={isCancelledModalOpen}
					closeModal={closeCancelledModal}
					confirm={handleCancelled}>
					<p>
						{t(
							'You are about to request "cancel" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isCompletedModalOpen ? (
				<ConfirmModal
					title={t('Request picked up')}
					isOpened={isCompletedModalOpen}
					closeModal={closeCompletedModal}
					confirm={handleCompleted}>
					<p>
						{t(
							'You are about to request "picked up" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isOverdueModalOpen ? (
				<ConfirmModal
					title={t('Request storage')}
					isOpened={isOverdueModalOpen}
					closeModal={closeOverdueModal}
					confirm={handleOverdue}>
					<p>
						{t(
							'You are about to request "storage" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isAbandonedModalOpen ? (
				<ConfirmModal
					title={t('Request donate')}
					isOpened={isAbandonedModalOpen}
					closeModal={closeAbandonedModal}
					confirm={handleAbandoned}>
					<p>
						{t(
							'You are about to request "donate" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isTakedownBookedModalOpen ? (
				<ConfirmModal
					title={t('Request takedown')}
					isOpened={isTakedownBookedModalOpen}
					closeModal={closeTakedownBookedModal}
					confirm={handleTakedown}>
					<p>
						{t(
							'You are about to request "takedown" for this booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			) : null}
			{isMoveUnsoldProductsModalOpen ? (
				<ConfirmModal
					title={t('Move unsold products')}
					isOpened={isMoveUnsoldProductsModalOpen}
					closeModal={() => {
						closeMoveUnsoldProductsModal()
						setValue('targetBooking', undefined)
						clearErrors('targetBooking')
					}}
					confirm={handleSubmit(onSubmit)}>
					<div className="flex flex-col gap-8">
						<p>
							{t(
								'Move all products with a quantity greater than 0 from booking {{ bookingId }} to another active booking for the same user and store. Note: Products can only be moved from completed bookings. Moving products from an active booking is not allowed.',
								{
									bookingId: booking?.data?.id ? `#${booking?.data?.id}` : ''
								}
							)}
						</p>
						<Controller
							name="targetBooking"
							control={control}
							render={({ field }) => (
								<Select
									id="targetBooking"
									variant="form"
									placeholder={t('None')}
									label={t('Move to booking')}
									required={true}
									error={(errors as any).targetBooking?.value}
									options={bookings?.map((target) => ({
										label: `#${target.ref_nr} - ${target.status} - ${format(
											new Date(target.start_date),
											'd MMMM yyyy',
											{ locale: language }
										)} ${t('to')} ${format(new Date(target.end_date), 'd MMMM yyyy', {
											locale: language
										})}`,
										value: target.id
									}))}
									{...field}
									onChange={(data) =>
										handleSelectChange({
											id: 'targetBooking',
											value: data?.value,
											label: data?.label
										})
									}
								/>
							)}
						/>
					</div>
				</ConfirmModal>
			) : null}
		</>
	)
}

export default BookingPage
